import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { Badge } from "../components/ui/badge";
import { ArrowDownToLine, Flame, ShieldCheck, AlertTriangle, Power, ArrowUp, ArrowDown, Minus, Thermometer } from "lucide-react";

const DIR_ICON = { 1: ArrowUp, up: ArrowUp, 0: Minus, idle: Minus, "-1": ArrowDown, down: ArrowDown };

/** Lifts Module Page — reads lift assets and renders firefighter status, GR compliance.
 *  On critical fire alarm we display a global recall banner. */
export default function LiftsModule() {
  const { sites, selectedSite } = useSiteContext();
  const [lifts, setLifts] = useState([]);
  const [fireActive, setFireActive] = useState(false);
  const [liveValues, setLiveValues] = useState({});

  const load = () => {
    const params = { asset_type: "lift" };
    if (selectedSite && selectedSite !== "all") params.site_id = selectedSite;
    api.get("/assets", { params }).then((r) => setLifts(r.data));
    api.get("/alerts", { params: { limit: 5 } }).then((r) => {
      const crits = (r.data || []).filter((a) => a.severity === "CRITICAL" && (a.status === "open" || a.is_active));
      setFireActive(crits.length > 0);
    }).catch(() => {});
    if (selectedSite && selectedSite !== "all") {
      api.get("/assets/live-values", { params: { site_id: selectedSite } }).then((r) => setLiveValues(r.data || {})).catch(() => {});
    }
  };
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, [selectedSite]);

  const today = new Date().toISOString().slice(0, 10);
  const dueIn = (d) => d ? Math.round((new Date(d) - new Date(today)) / 86400000) : null;
  const currentSite = sites.find((s) => s.site_id === selectedSite);

  return (
    <div className="space-y-6" data-testid="lifts-module-page">
      <div>
        <div className="text-xs text-gray-500">Dashboard{currentSite?.name ? ` → ${currentSite.name}` : ""} → Lifts Module</div>
        <h1 className="text-3xl md:text-4xl font-extrabold flex items-center gap-2"><ArrowDownToLine className="w-7 h-7 text-pink-400" strokeWidth={2.5} /> Lifts</h1>
        <p className="text-sm text-gray-500 mt-1">NBC 2016 Part-4 firefighter lift compliance · live recall status</p>
      </div>

      {fireActive && (
        <div className="bg-red-500 text-white px-6 py-4 rounded-lg alarm-pulse flex items-center gap-3" data-testid="fire-recall-banner">
          <Flame className="w-7 h-7" strokeWidth={2.5} />
          <div>
            <div className="text-xs uppercase tracking-widest font-bold opacity-90">FIRE SIGNAL ACTIVE</div>
            <div className="text-lg font-bold">All lifts in PHASE-1 RECALL → Ground Floor</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {lifts.map((L) => {
          const m = L.meta || {};
          const due = dueIn(m.next_service);
          const dueTone = due === null ? "text-slate-500" : due < 0 ? "text-red-700" : due <= 7 ? "text-amber-700" : "text-emerald-700";
          const phase1Tone = m.phase1_tested ? "text-emerald-700" : "text-amber-700";
          const phase2Tone = m.phase2_tested ? "text-emerald-700" : "text-amber-700";
          const mode = fireActive ? "Phase-1 Recall" : "Normal";
          const live = liveValues[L.asset_id];
          const currentFloor = fireActive ? (m.recall_floor ?? 0) : (live ? Number(live.value) : null);
          const direction = live?.extra?.direction;
          const doorStatus = live?.extra?.door_status;
          const motorTemp = live?.extra?.motor_temp;
          const DirIcon = direction ? (DIR_ICON[direction.value] || Minus) : null;
          return (
            <div key={L.asset_id} className="glass rounded-lg p-5 border-l-4 border-pink-400/30" data-testid={`lift-${L.asset_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-bold text-lg">{L.name}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">{(m.lift_type || "passenger").toUpperCase()} · {L.location_label}</div>
                </div>
                {m.lift_type === "firefighter" && <Badge className="bg-pink-50 text-pink-700 border-pink-200 text-[10px]">FIREFIGHTER</Badge>}
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500 text-[10px] uppercase">Mode</div><div className={`text-sm font-bold mt-1 ${fireActive ? "text-red-700" : "text-emerald-700"}`}>{mode}</div></div>
                <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500 text-[10px] uppercase">Floor</div><div className="text-2xl font-extrabold text-pink-700">{currentFloor !== null ? currentFloor : <span className="text-sm text-gray-400 font-semibold">{L.gateway_id ? "awaiting data" : "not linked"}</span>}</div></div>
                <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500 text-[10px] uppercase">Power</div><div className="text-sm font-bold text-emerald-700 mt-1">Mains</div></div>
              </div>

              {(direction || doorStatus || motorTemp) && (
                <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-gray-500 text-[10px] uppercase">Direction</div>
                    <div className="text-pink-700 mt-1.5 flex justify-center">{DirIcon ? <DirIcon className="w-5 h-5" strokeWidth={2.5} /> : "—"}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-gray-500 text-[10px] uppercase">Door</div>
                    <div className="text-sm font-bold mt-1 capitalize">{doorStatus ? (Number(doorStatus.value) === 1 ? "Open" : "Closed") : "—"}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-2">
                    <div className="text-gray-500 text-[10px] uppercase flex items-center justify-center gap-1"><Thermometer className="w-3 h-3" strokeWidth={2.5} /> Motor</div>
                    <div className="text-sm font-bold mt-1">{motorTemp ? `${Number(motorTemp.value).toFixed(1)}°C` : "—"}</div>
                  </div>
                </div>
              )}

              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Capacity</div><div className="font-bold text-gray-900">{m.capacity_kg || "—"} kg · {m.stops || "—"} stops</div></div>
                <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Recall Floor</div><div className="font-bold text-gray-900">{m.recall_floor ?? "—"}</div></div>
                <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Phase 1</div><div className={`font-bold ${phase1Tone}`}>{m.phase1_tested?.slice(0, 10) || "Not tested"}</div></div>
                <div className="bg-gray-50 rounded-lg p-2"><div className="text-gray-500">Phase 2</div><div className={`font-bold ${phase2Tone}`}>{m.phase2_tested?.slice(0, 10) || (m.firefighter_mode === "Phase1" ? "N/A" : "Not tested")}</div></div>
                <div className="bg-gray-50 rounded-lg p-2 col-span-2"><div className="text-gray-500">Next service</div><div className={`font-bold ${dueTone}`}>{m.next_service?.slice(0, 10) || "—"}{due !== null && (due < 0 ? ` · ${-due}d overdue` : ` · in ${due}d`)}</div></div>
              </div>
            </div>
          );
        })}
        {!lifts.length && <div className="col-span-full glass rounded-lg p-10 text-center text-gray-500 text-sm">No lifts in registry. Add lifts at Asset Registries → Lifts.</div>}
      </div>

      {/* GR Compliance Table */}
      <div className="glass rounded-lg p-5">
        <h2 className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-3 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5" strokeWidth={2.5} /> NBC 2016 Part-4 · Firefighter Lift Compliance</h2>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500">
            <tr><th className="text-left p-2">Lift</th><th className="text-left">Type</th><th className="text-left">Recall floor configured</th><th className="text-left">Phase 1</th><th className="text-left">Phase 2</th><th className="text-left">Compliance</th></tr>
          </thead>
          <tbody>
            {lifts.map((L) => {
              const m = L.meta || {};
              const recallOk = (m.recall_floor !== undefined && m.recall_floor !== null);
              const p1 = !!m.phase1_tested;
              const p2 = m.firefighter_mode === "Phase1" ? true : !!m.phase2_tested;
              const ok = recallOk && p1 && p2;
              return (
                <tr key={L.asset_id} className="border-t border-gray-200">
                  <td className="p-2">{L.name}</td>
                  <td className="text-xs uppercase">{m.lift_type || "—"}</td>
                  <td>{recallOk ? <span className="text-emerald-700">YES (floor {m.recall_floor})</span> : <span className="text-red-700">NO</span>}</td>
                  <td className="text-xs">{p1 ? <span className="text-emerald-700">{m.phase1_tested?.slice(0, 10)}</span> : <span className="text-amber-700">Not tested</span>}</td>
                  <td className="text-xs">{m.firefighter_mode === "Phase1" ? <span className="text-gray-500">N/A</span> : (p2 ? <span className="text-emerald-700">{m.phase2_tested?.slice(0, 10)}</span> : <span className="text-amber-700">Not tested</span>)}</td>
                  <td><Badge className={`text-[10px] ${ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>{ok ? "PASS" : "FAIL"}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
